import {
  allSectionsOf,
  getInstanceCount,
  instanceCountFromValues,
  instanceCountKey,
  mirrorSourceOf,
  sectionMinInstances,
  setInstanceCount,
} from '@bmo/form-core';

import type { WorkflowTemplate } from '@/api/workflows';

import type { OfferLanguages } from './clientLanguage';
import { applySectionInstanceDefaults } from './defaultOptions';
import type { FieldValue } from './FieldRenderer';

/**
 * "Repeat once per instance of" (RDSP QA D-39): keep every mirrored section's instance count
 * equal to its source's — two account holders, two Relationship-to-Beneficiary blocks.
 *
 * Only the `__instanceCount:<mirror>` sentinel is written. Every reader of a repeatable section
 * (renderer, review, snapshot, signer roster, PDF filler, completion checks) already sizes its
 * loop from that sentinel through `getInstanceCount`, so they all follow without knowing that
 * mirroring exists. A block that appears because a holder was added starts from its "Selected
 * by default" answers, exactly as one added with the Add button does.
 *
 * Returns the SAME object when every mirror is already in step, so it is safe inside a state
 * updater that runs on every values change.
 */
export function syncMirroredInstanceCounts(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  clientLanguage: OfferLanguages,
): Record<string, FieldValue> {
  const all = allSectionsOf(template);
  let next = values;
  for (const mirror of all) {
    const source = mirrorSourceOf(mirror, all);
    if (!source) continue;
    const want = getInstanceCount(next, source.id, sectionMinInstances(source));
    // How many instances the mirror actually HOLDS — its sentinel if it has one, else what
    // its `inst:` keys reach. Captured before the write below, which moves the sentinel.
    const have = instanceCountFromValues(next, mirror.id);
    // In step is decided on the SENTINEL, never on `have`. Writing that sentinel is this
    // module's entire job (see the note above), and `getInstanceCount` now derives a count
    // from the instance keys when it is missing — so comparing effective counts would let a
    // mirror that merely LOOKS in step, because its keys happen to reach `want`, go on
    // carrying no sentinel at all. Comparing the stored value also makes the pass idempotent
    // for a mirror whose own floor exceeds the source's count, which would otherwise rewrite
    // (and so return a new object) on every values change.
    if (next[instanceCountKey(mirror.id)] === want) continue;
    next = setInstanceCount(next, mirror.id, want);
    // Seed only the instances that do not exist yet, so a block already carrying answers is
    // never defaulted over.
    for (let k = have; k < want; k++) {
      next = applySectionInstanceDefaults(mirror, k, next, clientLanguage);
    }
  }
  return next;
}
